import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@asm/ui";
import {
  Globe,
  Server,
  Network,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Clock,
  AlertTriangle,
  Layers,
  ArrowRight,
  ExternalLink,
  Cloud,
  Sparkles,
  ShieldOff,
  Eye,
  EyeOff,
} from "lucide-react";
import { assetsApi, exceptionsApi, type Asset, type DomainAssetGroup } from "@/lib/api";

// Cloud provider icons mapping
const cloudProviderIcons: Record<string, { icon: string; color: string; name: string }> = {
  "amazon": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "aws": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
  "microsoft": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "azure": { icon: "/icons/azure.svg", color: "#0078D4", name: "Azure" },
  "google": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "gcp": { icon: "/icons/gcp.svg", color: "#4285F4", name: "GCP" },
  "digitalocean": { icon: "/icons/digitalocean.svg", color: "#0080FF", name: "DigitalOcean" },
  "cloudflare": { icon: "/icons/cloudflare.svg", color: "#F38020", name: "Cloudflare" },
  "linode": { icon: "/icons/linode.svg", color: "#00A95C", name: "Linode" },
  "akamai": { icon: "/icons/akamai.svg", color: "#0096D6", name: "Akamai" },
  "fastly": { icon: "/icons/fastly.svg", color: "#FF282D", name: "Fastly" },
  "ovh": { icon: "/icons/ovh.svg", color: "#123F6D", name: "OVH" },
  "hetzner": { icon: "/icons/hetzner.svg", color: "#D50C2D", name: "Hetzner" },
  "vultr": { icon: "/icons/vultr.svg", color: "#007BFC", name: "Vultr" },
  "oracle": { icon: "/icons/oracle.svg", color: "#F80000", name: "Oracle Cloud" },
  "ibm": { icon: "/icons/ibm.svg", color: "#1F70C1", name: "IBM Cloud" },
  "alibaba": { icon: "/icons/alibaba.svg", color: "#FF6A00", name: "Alibaba Cloud" },
};

// AWS service-specific icons
const awsServiceIcons: Record<string, { icon: string; color: string; name: string }> = {
  "cloudfront": { icon: "/icons/aws-cloudfront.svg", color: "#8C4FFF", name: "AWS CloudFront" },
  "ec2": { icon: "/icons/aws-ec2.svg", color: "#FF9900", name: "AWS EC2" },
  "s3": { icon: "/icons/aws-s3.svg", color: "#569A31", name: "AWS S3" },
  "lambda": { icon: "/icons/aws-lambda.svg", color: "#FF9900", name: "AWS Lambda" },
  "elb": { icon: "/icons/aws-elb.svg", color: "#8C4FFF", name: "AWS ELB" },
  "elasticloadbalancing": { icon: "/icons/aws-elb.svg", color: "#8C4FFF", name: "AWS ELB" },
  "globalaccelerator": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS Global Accelerator" },
  "apigateway": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS API Gateway" },
  "route53": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS Route 53" },
  "amazon": { icon: "/icons/aws.svg", color: "#FF9900", name: "AWS" },
};

function getCloudInfo(cloudProvider?: string, cloudService?: string): { icon: string; color: string; name: string } | null {
  if (!cloudProvider) return null;
  const providerTerm = cloudProvider.toLowerCase();

  // For AWS, check service-specific icons first
  if (providerTerm === "aws" || providerTerm === "amazon") {
    if (cloudService) {
      const serviceTerm = cloudService.toLowerCase();
      if (awsServiceIcons[serviceTerm]) {
        return awsServiceIcons[serviceTerm];
      }
    }
    // Fallback to generic AWS
    return cloudProviderIcons["aws"];
  }

  // Direct match for other providers
  if (cloudProviderIcons[providerTerm]) {
    return cloudProviderIcons[providerTerm];
  }
  // Partial match
  for (const [key, value] of Object.entries(cloudProviderIcons)) {
    if (providerTerm.includes(key) || key.includes(providerTerm)) {
      return value;
    }
  }
  return null;
}


// Simple relative time formatter (avoids date-fns dependency)
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""}`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? "s" : ""}`;
  return "just now";
}

type AssetTypeFilter = "all" | "subdomain" | "ip" | "port";
type StatusFilter = "all" | "active" | "stale" | "excluded";
type ViewMode = "all" | "grouped";

export default function AssetInventory() {
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [search, setSearch] = useState("");
  const [assetType, setAssetType] = useState<AssetTypeFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [showNew, setShowNew] = useState(false);
  const [hideExcluded, setHideExcluded] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 25;

  // Fetch exceptions to cross-reference with assets
  const { data: exceptionsData } = useQuery({
    queryKey: ["exceptions", "all"],
    queryFn: () => exceptionsApi.list({ per_page: 1000 }),
  });

  // Build a Set of "type:identifier" for O(1) lookup
  const exceptedSet = useMemo(() => {
    const set = new Set<string>();
    if (exceptionsData?.items) {
      for (const ex of exceptionsData.items) {
        set.add(`${ex.asset_type}:${ex.identifier.toLowerCase()}`);
      }
    }
    return set;
  }, [exceptionsData]);

  const isAssetExcepted = (asset: Asset) =>
    exceptedSet.has(`${asset.asset_type}:${asset.identifier.toLowerCase()}`);

  // Fetch asset statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["asset-stats"],
    queryFn: () => assetsApi.getStats(),
  });

  // Fetch assets list (for "All Assets" view)
  const { data: assetsData, isLoading: assetsLoading, refetch } = useQuery({
    queryKey: ["assets", assetType, status, search, showNew, hideExcluded, page],
    queryFn: () =>
      assetsApi.list({
        asset_type: assetType === "all" ? undefined : assetType,
        status: status === "all" ? undefined : status,
        search: search || undefined,
        is_new: showNew || undefined,
        exclude_excepted: hideExcluded || undefined,
        page,
        per_page: perPage,
      }),
    enabled: viewMode === "all",
  });

  // Fetch grouped assets (for "By Domain" view)
  const { data: groupedData, isLoading: groupedLoading, refetch: refetchGrouped } = useQuery({
    queryKey: ["assets-grouped"],
    queryFn: () => assetsApi.getGrouped(),
    enabled: viewMode === "grouped",
  });

  const totalPages = assetsData ? Math.ceil(assetsData.total / perPage) : 0;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.total_count || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {stats?.active_count || 0} active
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subdomains</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{stats?.subdomains_count || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">IP Addresses</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{stats?.ips_count || 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <>
                <div className="text-2xl font-bold">{stats?.new_this_week || 0}</div>
                {(stats?.stale_count || 0) > 0 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-500">
                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                    {stats?.stale_count} stale
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Tabs */}
      <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              <Layers className="h-4 w-4" />
              All Assets
            </TabsTrigger>
            <TabsTrigger value="grouped" className="gap-2">
              <Globe className="h-4 w-4" />
              By Domain
            </TabsTrigger>
          </TabsList>
          <Button
            onClick={() => viewMode === "all" ? refetch() : refetchGrouped()}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* All Assets View */}
        <TabsContent value="all" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search assets..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className="pl-10"
                  />
                </div>

                <Select
                  value={assetType}
                  onValueChange={(v: AssetTypeFilter) => {
                    setAssetType(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Asset Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="subdomain">Subdomains</SelectItem>
                    <SelectItem value="ip">IP Addresses</SelectItem>
                    <SelectItem value="port">Ports</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={status}
                  onValueChange={(v: StatusFilter) => {
                    setStatus(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="stale">Stale</SelectItem>
                    <SelectItem value="excluded">Excluded</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant={showNew ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setShowNew(!showNew);
                    setPage(1);
                  }}
                  className="gap-1.5"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  New
                </Button>

                <Button
                  variant={hideExcluded ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setHideExcluded(!hideExcluded);
                    setPage(1);
                  }}
                  className="gap-1.5"
                >
                  {hideExcluded ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {hideExcluded ? "Show Excluded" : "Hide Excluded"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Assets List */}
          <Card>
            <CardHeader>
              <CardTitle>Assets</CardTitle>
              <CardDescription>
                {assetsData?.total || 0} assets found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assetsLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : assetsData?.items.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No assets found</p>
                  <p className="text-sm">
                    Assets will appear here after your first scan
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Header Row */}
                  <div className="grid grid-cols-[auto_auto_minmax(120px,1fr)_160px_minmax(180px,1fr)_110px_70px_auto] items-center gap-3 px-4 py-2 text-xs font-medium text-muted-foreground border-b">
                    <div className="w-6 text-center">Cloud</div>
                    <div className="w-10 text-center">Type</div>
                    <div>Identifier</div>
                    <div>Type / Service</div>
                    <div>Hostnames</div>
                    <div>Last Seen</div>
                    <div className="text-center">Status</div>
                    <div className="w-4"></div>
                  </div>
                  {assetsData?.items
                    .filter((asset) => {
                      if (status === "excluded") return isAssetExcepted(asset);
                      return true;
                    })
                    .map((asset) => (
                    <AssetRow key={asset.id} asset={asset} isExcepted={isAssetExcepted(asset)} />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Grouped by Domain View */}
        <TabsContent value="grouped" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Assets by Domain</CardTitle>
              <CardDescription>
                View assets organized by their root domain
              </CardDescription>
            </CardHeader>
            <CardContent>
              {groupedLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full" />
                  ))}
                </div>
              ) : !groupedData || groupedData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No domains with assets</p>
                  <p className="text-sm">
                    Add domains and run scans to discover assets
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {groupedData.map((group) => (
                    <DomainGroupCard key={group.domain_id} group={group} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AssetRow({ asset, isExcepted }: { asset: Asset; isExcepted: boolean }) {
  const getAssetIcon = (type: string) => {
    switch (type) {
      case "subdomain":
        return <Globe className="h-4 w-4 text-blue-500" />;
      case "ip":
        return <Server className="h-4 w-4 text-green-500" />;
      case "port":
        return <Network className="h-4 w-4 text-purple-500" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="default" className="bg-green-500/10 text-green-600 hover:bg-green-500/20">Active</Badge>;
      case "stale":
        return <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600">Stale</Badge>;
      case "removed":
        return <Badge variant="destructive">Removed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get cloud provider info from metadata
  const cloudProvider = asset.metadata?.cloud_provider as string | undefined;
  const cloudService = asset.metadata?.cloud_service as string | undefined;
  const isCloud = asset.metadata?.is_cloud as boolean | undefined;
  const cloudInfo = getCloudInfo(cloudProvider, cloudService);

  // Get FQDNs (hostnames) associated with this IP/port
  const fqdns = (asset.metadata?.fqdns as string[] | undefined) || [];

  return (
    <Link to={`/assets/${asset.id}`}>
      <div className={`grid grid-cols-[auto_auto_minmax(120px,1fr)_160px_minmax(180px,1fr)_110px_70px_auto] items-center gap-3 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group ${isExcepted ? "opacity-50" : ""}`}>
        {/* Column 1: Cloud Icon */}
        <div className="w-6 flex items-center justify-center">
          {(asset.asset_type === "ip" || asset.asset_type === "port") && (
            cloudInfo ? (
              <img
                src={cloudInfo.icon}
                alt={cloudInfo.name}
                className="h-5 w-5"
                title={cloudInfo.name}
              />
            ) : isCloud ? (
              <Cloud className="h-5 w-5 text-muted-foreground" />
            ) : null
          )}
        </div>

        {/* Column 2: Asset Type Icon */}
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted shrink-0">
          {getAssetIcon(asset.asset_type)}
        </div>

        {/* Column 3: Identifier */}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-medium font-mono text-sm truncate" title={asset.identifier}>
              {asset.identifier}
            </p>
            {asset.first_seen_at === asset.last_seen_at && (
              <Badge variant="default" className="bg-blue-500/10 text-blue-600 text-[10px] px-1.5 py-0 shrink-0">
                NEW
              </Badge>
            )}
          </div>
        </div>

        {/* Column 4: Type Info (Type · Cloud Service) */}
        <div className="text-xs">
          <span className="text-muted-foreground">
            {asset.asset_type === "ip" ? "IP" : asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1)}
          </span>
          {cloudInfo && (
            <>
              <span className="text-muted-foreground"> · </span>
              <span className="text-blue-500">{cloudInfo.name}</span>
            </>
          )}
        </div>

        {/* Column 5: Hostnames (FQDNs) */}
        <div className="min-w-0">
          {(asset.asset_type === "ip" || asset.asset_type === "port") && fqdns.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {fqdns.slice(0, 3).map((fqdn, i) => (
                <span
                  key={i}
                  className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded"
                >
                  {fqdn}
                </span>
              ))}
              {fqdns.length > 3 && (
                <span className="text-xs text-muted-foreground">+{fqdns.length - 3}</span>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>

        {/* Column 6: Last Seen */}
        <div className="text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 shrink-0" />
            <span>{formatRelativeTime(new Date(asset.last_seen_at))} ago</span>
          </div>
        </div>

        {/* Column 7: Status */}
        <div className="flex justify-center">
          {isExcepted ? (
            <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/30 gap-1">
              <ShieldOff className="h-3 w-3" />
              Excluded
            </Badge>
          ) : (
            getStatusBadge(asset.status)
          )}
        </div>

        {/* Column 8: Arrow */}
        <div>
          <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </Link>
  );
}

function DomainGroupCard({ group }: { group: DomainAssetGroup }) {
  return (
    <Link to={`/domains/${group.domain_id}/assets`}>
      <div className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-lg">{group.domain_name}</p>
              <p className="text-sm text-muted-foreground">
                {group.total_count} total assets
              </p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>

        {/* Asset type breakdown */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500" />
            <span className="text-sm">
              <span className="font-medium">{group.subdomain_count}</span>
              <span className="text-muted-foreground ml-1">subdomains</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-green-500" />
            <span className="text-sm">
              <span className="font-medium">{group.ip_count}</span>
              <span className="text-muted-foreground ml-1">IPs</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-purple-500" />
            <span className="text-sm">
              <span className="font-medium">{group.port_count}</span>
              <span className="text-muted-foreground ml-1">ports</span>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
